import React, { useCallback, useEffect, useState } from 'react';
import { Users, Plus, Loader2, X, Sparkles } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import type { OrgChartData, OrgDeptNode, OrgMember, CohortSuggestion } from '../../types/aai';
import { Loading, ErrorNote } from './components';
import { Card, MonoLabel, PageHeader } from './dashboards/ui';

// Module 5 — the AI-adoption org chart. Design thesis: "roles not titles" — group
// people by adoption-role TIER (Executive Sponsor → AI Champion → Peer Coach →
// Employee), never by job title, never readiness. Champion networks are the point.
// The peer-coaching edge list below stays wired to assign/end mentorship.

type TierKey = 'sponsor' | 'champion' | 'coach' | 'employee';

// Colored dot + legend chip per tier, matched to the mockup (Sponsor amber,
// Champion brand-blue, Peer Coach green, Employee gray) in the app's Tailwind.
const TIERS: { key: TierKey; label: string; dot: string; legend: string }[] = [
  { key: 'sponsor',  label: 'Executive Sponsor', dot: 'bg-amber-400',   legend: 'bg-amber-50 text-amber-700' },
  { key: 'champion', label: 'AI Champion',       dot: 'bg-brand-500',   legend: 'bg-brand-50 text-brand-700' },
  { key: 'coach',    label: 'Peer Coach',        dot: 'bg-emerald-500', legend: 'bg-emerald-50 text-emerald-700' },
  { key: 'employee', label: 'Employee',          dot: 'bg-gray-300',    legend: 'bg-gray-100 text-gray-600' },
];

// Map the API's adoption_role (+ is_coach) into one of the four design tiers.
// Defensive: substring match so label variants still land correctly. Anything
// unmodeled by the four tiers (Expert, Department Head, unknown) reads as Employee.
const tierOf = (m: OrgMember): TierKey => {
  const role = (m.adoption_role || '').toLowerCase();
  if (role.includes('sponsor') || role.includes('executive')) return 'sponsor';
  if (role.includes('champion')) return 'champion';
  if (m.is_coach || role.includes('coach')) return 'coach';
  return 'employee';
};

// The design's four cohort cuts (keys match the backend CUT_TYPES). "availability"
// has no generator server-side yet, so its pill simply shows an empty state.
const CUT_MODES: { key: string; label: string }[] = [
  { key: 'team', label: 'By team' },
  { key: 'availability', label: 'By availability' },
  { key: 'readiness', label: 'By readiness' },
  { key: 'tool_usage', label: 'By tool-usage pattern' },
];

const initials = (name: string): string =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('') || '?';

// A member enriched with the department it came from and its coach count.
interface OrgPerson extends OrgMember {
  department: string | null;
  mentors: number;
}

const PersonChip: React.FC<{ p: OrgPerson; tierLabel: string }> = ({ p, tierLabel }) => {
  const meta = [p.department, p.mentors > 0 ? `mentors ${p.mentors}` : null].filter(Boolean).join(' · ');
  // Surface the real adoption role only when the tier collapses it (e.g. a
  // Department Head or Expert reading in the Employee band). Never invent one.
  const showRole = !!p.adoption_role && p.adoption_role !== tierLabel;
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-gray-100 px-3 py-2">
      <span className="w-8 h-8 rounded-full bg-gray-100 grid place-items-center text-[11px] font-bold text-gray-600 shrink-0">
        {initials(p.name)}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[12.5px] font-medium text-gray-800">
          <span className="truncate">{p.name}</span>
          {p.onboarded && (
            <span title="Onboarded" className="text-emerald-500 text-[15px] leading-none shrink-0">•</span>
          )}
          {showRole && (
            <span className="shrink-0 rounded-full bg-gray-100 text-gray-500 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide">
              {p.adoption_role}
            </span>
          )}
        </span>
        {meta && <span className="block text-[11px] text-gray-400 truncate">{meta}</span>}
      </span>
    </div>
  );
};

const OrgChartPage: React.FC = () => {
  const { context } = useParticipant();
  const canManage = !!context?.capabilities?.manage_program;
  const [data, setData] = useState<OrgChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coach, setCoach] = useState('');
  const [learner, setLearner] = useState('');
  const [busy, setBusy] = useState(false);
  // Cohort builder (champion lens) — Orbit's suggested cuts, keyed by cut mode.
  const [suggestions, setSuggestions] = useState<CohortSuggestion[]>([]);
  const [selectedCut, setSelectedCut] = useState('tool_usage');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oc, sg] = await Promise.all([
        aaiApi.getOrgChart(),
        aaiApi.suggestCohorts().catch(() => ({ data: { suggestions: [] as CohortSuggestion[] } })),
      ]);
      setData(oc.data); setSuggestions(sg.data.suggestions || []); setError(null);
    } catch { setError('Could not load the org chart.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const createSuggested = async (sg: CohortSuggestion) => {
    setCreating(true);
    try {
      await aaiApi.createCohort({ name: sg.name, cut_type: sg.cut_type, criteria: sg.criteria, member_emails: sg.members });
      const r = await aaiApi.suggestCohorts().catch(() => ({ data: { suggestions: [] as CohortSuggestion[] } }));
      setSuggestions(r.data.suggestions || []);
    } finally { setCreating(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto"><Loading label="Loading the org chart…" /></div>;
  if (error || !data) return <div className="max-w-4xl mx-auto"><ErrorNote message={error || 'Something went wrong.'} /></div>;

  // Coach counts, derived from the real mentorship edges (never fabricated).
  const mentorCounts: Record<string, number> = {};
  for (const e of data.edges) {
    const key = (e.coach_email || '').toLowerCase();
    mentorCounts[key] = (mentorCounts[key] || 0) + 1;
  }

  // Flatten departments + unassigned into people, keeping each person's home
  // department and coach count, then re-group by adoption tier.
  const flatten = (nodes: OrgDeptNode[]): OrgPerson[] =>
    nodes.flatMap((d) => [
      ...d.members.map((m) => ({ ...m, department: d.name, mentors: mentorCounts[(m.email || '').toLowerCase()] || 0 })),
      ...flatten(d.children || []),
    ]);
  const people: OrgPerson[] = [
    ...flatten(data.departments),
    ...data.unassigned.map((m) => ({ ...m, department: null, mentors: mentorCounts[(m.email || '').toLowerCase()] || 0 })),
  ];
  const grouped: Record<TierKey, OrgPerson[]> = { sponsor: [], champion: [], coach: [], employee: [] };
  for (const p of people) grouped[tierOf(p)].push(p);

  const allMembers: OrgPerson[] = people;
  const assign = async () => {
    if (!coach || !learner || coach === learner) return;
    setBusy(true);
    try { await aaiApi.assignMentorship(coach, learner); setCoach(''); setLearner(''); await load(); }
    finally { setBusy(false); }
  };
  const endEdge = async (id: string) => { setBusy(true); try { await aaiApi.endMentorship(id); await load(); } finally { setBusy(false); } };

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="Org chart"
        subtitle="Who's who in your AI adoption — sponsors, champions, coaches, and learners. Roles and structure, never scores."
      />

      <div className="space-y-4">
        {/* ROL-3: HR's actual org structure — departments (nested where a parent
            is set) with each person's job title. Structure and roles only. */}
        {data.departments.length > 0 && (
          <Card>
            <MonoLabel>Company structure</MonoLabel>
            <p className="text-[12px] text-gray-400 mt-1 mb-4 max-w-md">
              The org chart as HR supplied it — departments, sub-departments, people and their job titles.
            </p>
            <div className="space-y-3">
              {data.departments.map((d) => <DeptNode key={d.name} node={d} depth={0} />)}
            </div>
          </Card>
        )}

        {/* Role-tier tree — people grouped by adoption role, not job title. */}
        <Card>
          <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
            <div>
              <MonoLabel>AI-adoption org chart</MonoLabel>
              <p className="text-[12px] text-gray-400 mt-1 max-w-md">
                Who's who in your AI adoption — grouped by role, not job title. Coaching is shown as a mentor count.
              </p>
            </div>
            <span className="font-sans text-[10px] uppercase tracking-wider text-gray-400 whitespace-nowrap">
              {people.length} {people.length === 1 ? 'person' : 'people'}
            </span>
          </div>

          {/* Role legend */}
          <div className="flex flex-wrap gap-2 mb-5">
            {TIERS.map((t) => (
              <span key={t.key} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold ${t.legend}`}>
                <span className={`w-2 h-2 rounded-sm ${t.dot}`} />
                {t.label}
              </span>
            ))}
          </div>

          {people.length === 0 ? (
            <p className="text-[13px] text-gray-400">No one on the adoption org chart yet.</p>
          ) : (
            <div className="space-y-5">
              {TIERS.map((t) => {
                const list = grouped[t.key];
                return (
                  <div key={t.key}>
                    <div className="flex items-center gap-2 mb-2.5">
                      <span className={`w-2.5 h-2.5 rounded-sm ${t.dot}`} />
                      <span className="font-sans text-[10.5px] uppercase tracking-wider font-semibold text-gray-600">{t.label}</span>
                      <span className="text-[11px] text-gray-400">{list.length}</span>
                      <div className="flex-1 h-px bg-gray-100" />
                    </div>
                    {list.length === 0 ? (
                      <p className="text-[11.5px] text-gray-300 pl-4">No one in this tier yet.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {list.map((p) => <PersonChip key={p.email} p={p} tierLabel={t.label} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Cohort builder (champion lens) — cut four ways; Orbit suggests, the
            client decides. Real suggestCohorts data; participants don't see it. */}
        {canManage && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <MonoLabel className="mb-1">Cohort builder</MonoLabel>
              <p className="text-[12px] text-gray-500 mb-3">Cut four ways — the system suggests, you decide.</p>
              <div className="flex flex-wrap gap-2">
                {CUT_MODES.map((cm) => (
                  <button key={cm.key} onClick={() => setSelectedCut(cm.key)}
                    className={`rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold border transition-colors ${selectedCut === cm.key ? 'bg-brand-500 border-brand-500 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-brand-200'}`}>
                    {cm.label}
                  </button>
                ))}
              </div>
            </Card>
            {(() => {
              const sug = suggestions.find((s) => s.cut_type === selectedCut);
              return (
                <div className="bg-black text-white rounded-xl p-5 flex flex-col">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2"><Sparkles size={16} className="text-accent-500" /><span className="font-bold text-[14px]">Suggested cohort</span></div>
                    {sug && <span className="bg-accent-500 text-black text-[11px] font-bold rounded-full px-2.5 py-0.5">{sug.members.length} {sug.members.length === 1 ? 'person' : 'people'}</span>}
                  </div>
                  {sug ? (
                    <>
                      <div className="font-bold text-[15px] mb-1.5">{sug.name}</div>
                      <p className="text-[12.5px] text-white/65 leading-relaxed m-0 mb-4">Orbit surfaced this cohort from real signals — a group no manager would assemble by hand.</p>
                      <button disabled={creating} onClick={() => createSuggested(sug)}
                        className="self-start text-[12.5px] font-semibold bg-white text-black rounded-full px-4 py-2 hover:bg-gray-100 disabled:opacity-50">
                        {creating ? 'Creating…' : 'Create this cohort'}
                      </button>
                    </>
                  ) : (
                    <p className="text-[12.5px] text-white/60 leading-relaxed m-0">No suggested cohort for this cut right now — Orbit surfaces them as people onboard and log the tools they use.</p>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Peer coaching — real mentorship edges, assign/end wired to the API. */}
        <Card>
          <div className="flex items-center gap-2 mb-3"><Users size={15} className="text-brand-500" />
            <MonoLabel>Peer coaching</MonoLabel></div>
          {data.edges.length === 0 ? (
            <p className="text-[13px] text-gray-400">No peer-coaching pairs yet.</p>
          ) : (
            <div className="space-y-1.5">
              {data.edges.map((e) => (
                <div key={e.id} className="flex items-center gap-2 text-[13px] text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                  <span className="font-medium">{e.coach_name}</span>
                  <span className="text-gray-400">coaches</span>
                  <span className="font-medium">{e.learner_name}</span>
                  {canManage && (
                    <button onClick={() => endEdge(e.id)} disabled={busy} title="End this pairing"
                      className="ml-auto text-gray-300 hover:text-red-500 disabled:opacity-50"><X size={14} /></button>
                  )}
                </div>
              ))}
            </div>
          )}

          {canManage && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-gray-50">
              <select value={coach} onChange={(e) => setCoach(e.target.value)}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200">
                <option value="">Coach…</option>
                {allMembers.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
              </select>
              <span className="text-[12px] text-gray-400">coaches</span>
              <select value={learner} onChange={(e) => setLearner(e.target.value)}
                className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-200">
                <option value="">Employee…</option>
                {allMembers.map((m) => <option key={m.email} value={m.email}>{m.name}</option>)}
              </select>
              <button onClick={assign} disabled={busy || !coach || !learner || coach === learner}
                className="inline-flex items-center gap-1 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-[12px] font-semibold px-3 py-1.5 whitespace-nowrap">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={13} />} Pair
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

// One department (and its sub-departments) from HR's hierarchy.
const DeptNode: React.FC<{ node: OrgDeptNode; depth: number }> = ({ node, depth }) => (
  <div style={{ marginLeft: depth ? 16 : 0 }} className={depth ? 'border-l-2 border-gray-100 pl-3' : ''}>
    <p className="text-[13px] font-semibold text-gray-900">{node.name}
      <span className="ml-1.5 text-[11px] font-normal text-gray-400">{node.members.length || ''}</span>
    </p>
    {node.members.length > 0 && (
      <ul className="mt-1 mb-2 space-y-0.5">
        {node.members.map((m) => (
          <li key={m.email} className="text-[12.5px] text-gray-600 truncate">
            {m.name}
            {m.job_title && <span className="text-gray-400"> — {m.job_title}</span>}
          </li>
        ))}
      </ul>
    )}
    {(node.children || []).map((c) => <DeptNode key={c.name} node={c} depth={depth + 1} />)}
  </div>
);

export default OrgChartPage;
